import { useEffect, useMemo, useRef, useState } from "react";
import { createSavedCurrencyFormatter } from "shared-ui/src/functions/currency";
import { StackedProductDetailDrawer } from "shared-ui/src/components/detail-drawer/StackedProductDetailDrawer";
import type { StackedDrawerProps } from "shared-ui/src/components/detail-drawer/drawer-stack";
import { ProductDrawerTab } from "shared-ui/src/components/ecommerce/product-detail-drawer/types";
import { getAddToCartDisabledReason } from "shared-ui/src/components/ecommerce/product-detail-drawer/addToCartDisabledReason";
import { useOrderProductDetailDrawer } from "@features/orders-features/hooks/useOrderProductDetailDrawer";
import useProductAvailability from "@features/ecommerce/hooks/useProductAvailability";
import AddToCartModal from "@features/ecommerce/cart/AddToCartModal";
import useAddToCartModal from "@features/ecommerce/cart/useAddToCartModal";
import { useClaims } from "../../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../../auth/authorizationTypes";
import { ProductDetailDrawerRequestDto } from "@lib/ShiOneClient";

export interface StackedOrderProductDetailDrawerProps
  extends StackedDrawerProps {
  /** Basic product info from the order line item */
  product: {
    productName?: string | null;
    mfrPartNum?: string | null;
    productDescriptor?: string | null;
    productClass?: string | null;
    price?: number | null;
  } | null;
  /** Request DTO for fetching enriched product data */
  request: ProductDetailDrawerRequestDto | null;
  currencyCode?: string | number | null;
  /** Shows the loading state while the caller is still building `request`. */
  loading?: boolean;
  /** Telemetry event section. Defaults to `orders_stacked_quick_view`. */
  telemetrySection?: string;
  /** Tab to show; the Stock / Inventory tab also starts the availability fetch. */
  initialTab?: ProductDrawerTab;
  /** Formats prices using the currency saved on the source order. */
  formatPrice?: (amount: number) => string;
  onTabChange?: (tab: ProductDrawerTab) => void;
  /**
   * Called once per product when its details fail to load or the catalog has no record. Omit
   * it to keep showing the basic product info passed in `product`.
   */
  onDetailUnavailable?: () => void;
  /** Called when the product's details have loaded, so the caller knows it will stay open. */
  onDetailAvailable?: () => void;
}

/**
 * A stacked product detail drawer that fetches enriched product data
 * (images, specs, descriptions) the same way the ViewOrdersPage product drawer does.
 * This ensures the same level of detail -- including the quantity selector, add-to-cart, and
 * Stock / Inventory tab -- is shown whether the product drawer is opened from the orders grid
 * or from within the order drawer.
 */
export function StackedOrderProductDetailDrawer({
  open,
  onClose,
  onCloseAll,
  zIndex,
  depth,
  product,
  request,
  currencyCode,
  loading = false,
  telemetrySection,
  initialTab,
  formatPrice,
  onTabChange,
  onDetailUnavailable,
  onDetailAvailable,
}: StackedOrderProductDetailDrawerProps) {
  const {
    isLoading: isDetailLoading,
    isError,
    hasProductDetail,
    isLoadingFallbackImages,
    enrichedProduct,
    liveCatalogPrice,
    images,
    specification,
    fullSpecification,
    productDetailUrl,
  } = useOrderProductDetailDrawer({ product, request });

  const currencyFormatter = useMemo(
    () => createSavedCurrencyFormatter(currencyCode),
    [currencyCode],
  );
  const orgUnitId = request?.orgunitId ?? null;
  const productDescriptor = request?.productDescriptor ?? null;
  const productClass = request?.productClass ?? null;

  const isLoading = loading || isDetailLoading;

  const [stockActivated, setStockActivated] = useState(false);
  useEffect(() => {
    setStockActivated(false);
  }, [productDescriptor]);
  const isStockRequested =
    stockActivated || initialTab === ProductDrawerTab.StockInventory;

  const availability = useProductAvailability(
    productDescriptor,
    orgUnitId,
    open && isStockRequested && productDescriptor != null && orgUnitId != null,
  );

  const unavailableKey =
    request != null && !isLoading && (isError || !hasProductDetail)
      ? `${productClass ?? ""}~${productDescriptor ?? ""}`
      : null;
  const reportedUnavailableKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      !open ||
      !onDetailUnavailable ||
      unavailableKey == null ||
      reportedUnavailableKeyRef.current === unavailableKey
    ) {
      return;
    }
    reportedUnavailableKeyRef.current = unavailableKey;
    onDetailUnavailable();
  }, [open, onDetailUnavailable, unavailableKey]);

  const isDetailAvailable =
    request != null && !isLoading && !isError && hasProductDetail;
  useEffect(() => {
    if (open && isDetailAvailable) {
      onDetailAvailable?.();
    }
  }, [open, isDetailAvailable, onDetailAvailable]);

  const { hasPermission } = useClaims();
  const { addToCart, modalProps } = useAddToCartModal();
  const addToCartDisabledReason = getAddToCartDisabledReason({
    isLoading,
    isError,
    // Matches the add endpoint's ShoppingCartSave gate (EcommerceShoppingCartController "add").
    hasCartPermission: hasPermission(ShiOnePermission.shoppingCartSave),
    hasProductIdentifiers: !!productDescriptor && !!productClass,
    canAddToCart: enrichedProduct?.canAddToCart,
    containedInCatalog: enrichedProduct?.containedInCatalog,
    hasCatalogDetail: enrichedProduct?.hasCatalogDetail,
  });
  const canAddToCart = addToCartDisabledReason == null;

  return (
    <>
      <StackedProductDetailDrawer
        open={open}
        onClose={onClose}
        onCloseAll={onCloseAll}
        zIndex={zIndex}
        depth={depth}
        loading={isLoading}
        isLoadingFallbackImages={isLoadingFallbackImages}
        product={enrichedProduct}
        images={images}
        fullSpecification={fullSpecification}
        specification={specification}
        productDetailUrl={productDetailUrl}
        formatPrice={formatPrice ?? currencyFormatter.format}
        telemetrySection={telemetrySection}
        initialTab={initialTab}
        onTabChange={(tab) => {
          if (tab === ProductDrawerTab.StockInventory) setStockActivated(true);
          onTabChange?.(tab);
        }}
        cart={{
          canAddToCart,
          disabledReason: addToCartDisabledReason,
          onAddToCart: (quantity) => {
            if (!productDescriptor || !productClass) return;
            addToCart({
              id: productDescriptor,
              productDescriptor,
              productClass,
              name: enrichedProduct?.productName ?? null,
              price: liveCatalogPrice ?? null,
              imageUrl: images[0]?.url ?? null,
              quantity,
            });
          },
        }}
        stockTab={{
          data: availability.data,
          // The availability query cannot start until the caller has built `request`.
          isLoading: loading || availability.isLoading,
          isError: availability.isError,
          onRefresh: availability.refetch,
        }}
      />
      <AddToCartModal {...modalProps} />
    </>
  );
}
